import React from "react";
import { useHistory } from "react-router-dom";
import Modal from "react-bootstrap/Modal";
import styled, { css } from "styled-components";
import {
  CantPlay,
  CantPlayAction,
  didProfileCompleteLesson,
  FullAccess,
  getVersionForLessonInProgress,
  hasAgeRange,
  isInTrialMode,
  isSchoolUserTeacher,
  Lesson,
} from "../common/api";
import { useParams } from "react-router-dom";
import { ApiContext } from "../common/context/ApiContext";
import { LessonPagePlayer } from "./LessonPagePlayer";
import { useLocation } from "react-router-dom";
import { useLiveData, useMediaQuery } from "../common/utils";
import { Account, AccountContext } from "../common/context/AccountContext";
import { useLoadingIndicator } from "../common/context/LoadingIndicatorContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlay } from "@fortawesome/pro-solid-svg-icons";
import {
  faCircleEllipsis,
  faPaperPlaneTop,
  faUserXmark,
  faArrowUpFromSquare,
  faPrint,
  faGraduationCap,
} from "@fortawesome/pro-regular-svg-icons";
import LessonStandardsModal from "../components/lesson/LessonStandardsModal";
import { ageRange } from "../common/utils";
import { useTranslation } from "react-i18next";
import { Button, Dropdown, Form } from "react-bootstrap";
import {
  BackNavRow,
  CallToActionButton,
  CellCover,
  CellMetaItem,
  CellMetaRow,
  DetailsTitle,
  PlayButton,
  SectionLabel,
} from "../common/CommonStyles";
import { default as UUID } from "uuid";
import { LessonPageEndLessonScreen } from "./LessonPageEndLessonScreen";
import { LessonAwards } from "../components/lesson/LessonAwards";
import { PrintablesModal } from "../components/lesson/PrintablesModal";
import { CompletedMetaItem } from "../components/LessonItemBody";
import { FlashpackEditMenu } from "../components/flashpacks/FlashpackEditMenu";
import { FlashpackSettings } from "../components/flashpacks/FlashpackSettings";
import { TabEnum } from "../common/context/TabsContext";
import { ConfirmRenameAndShareFlashpackModal } from "../components/lesson/ConfirmShareAndRenameFlashpackModal";
import {
  AnalyticsContext,
  AssignLessonButtonTapEvent,
  UnassignLessonButtonTapEvent,
  LessonMaterialsButtonTapEvent,
  LessonStandardsButtonTapEvent,
  LessonStartEditFlashpackButtonTapEvent,
  StartLessonButtonTapEvent,
  ViewLessonStartEvent,
} from "../common/context/AnalyticsContext";
import { SingleLessonUnassignModal } from "../components/assignments/SingleLessonUnassignModal";
import { LessonAssignmentsPage } from "./LessonAssignmentsPage";
import {
  COLOR_TEXT_PRIMARY,
  COLOR_TEXT_SECONDARY,
  COLOR_GRAY_DISABLED,
  COLOR_SURFACE_CONTAINER,
} from "../common/colors";
import {
  STACKED_CONTENT_MAX_WIDTH,
  WIDE_LAYOUT_QUERY,
} from "../common/constants";
import {
  SPACE_XS,
  SPACE_SM,
  SPACE_MD,
  SPACE_LG,
  SPACE_XL,
  SPACE_2XL,
  SPACE_4XL,
} from "../common/spacing";

const enum StartButtonState {
  UPGRADE,
  CONTINUE,
  START,
}

type LessonExecutionStateAfterPlay = {
  type: "AFTER_PLAY";
  readonly cantPlay: CantPlay | null;
};

export type LessonExecutionState =
  | { type: "BEFORE_PLAY_LESSON_NOT_YET_STARTED" }
  | { type: "BEFORE_PLAY_FIRSTNAME" }
  | { type: "BEFORE_PLAY_SHOW_FLASHPACK_SETTINGS" }
  | { type: "BEFORE_PLAY_ASSIGN_LESSON" }
  | { type: "PLAYING" }
  | LessonExecutionStateAfterPlay;

const DETAILS_WIDE_MAX_WIDTH = 1000;
const STACKED_COVER_PERCENT = 62;
const STACKED_COVER_SIZE_MIN = 220;
const COVER_SIZE_MAX = 360;
const WIDE_COVER_SIZE_MIN = 240;

const toolbarButton = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background-color: transparent;
  color: ${COLOR_TEXT_PRIMARY};
  font-size: 18px;

  :hover,
  :focus-visible {
    background-color: ${COLOR_SURFACE_CONTAINER};
    color: ${COLOR_TEXT_PRIMARY};
  }
`;

const ToolbarActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${SPACE_XS}px;
`;

const ToolbarButton = styled.button`
  ${toolbarButton}
`;

const ToolbarDropdownToggle = styled(Dropdown.Toggle)`
  ${toolbarButton}

  &::after {
    display: none;
  }
`;

const DetailsLayout = styled.div<{ $isWide: boolean }>`
  display: flex;
  flex-direction: ${(p) => (p.$isWide ? "row" : "column")};
  align-items: ${(p) => (p.$isWide ? "flex-start" : "stretch")};
  column-gap: clamp(${SPACE_XL}px, 3.5vw, ${SPACE_4XL}px);
  max-width: ${(p) =>
    p.$isWide ? DETAILS_WIDE_MAX_WIDTH : STACKED_CONTENT_MAX_WIDTH}px;
  margin: ${SPACE_LG}px auto 0;
  padding-bottom: ${SPACE_2XL}px;
`;

const CoverColumn = styled.div<{ $isWide: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${SPACE_XL}px;
  ${(p) =>
    p.$isWide &&
    css`
      flex: 0 0 clamp(${WIDE_COVER_SIZE_MIN}px, 26vw, ${COVER_SIZE_MAX}px);
      min-width: 0;
    `}
`;

const DetailsCover = styled(CellCover)<{ $isWide: boolean }>`
  width: ${(p) =>
    p.$isWide
      ? "100%"
      : `clamp(${STACKED_COVER_SIZE_MIN}px, ${STACKED_COVER_PERCENT}%, ${COVER_SIZE_MAX}px)`};
`;

const DetailsColumn = styled.div<{ $isWide: boolean }>`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  padding-top: ${(p) => (p.$isWide ? 0 : SPACE_2XL)}px;
`;

const DetailsMetaRow = styled(CellMetaRow)`
  justify-content: center;
  flex-wrap: wrap;
  margin-top: ${SPACE_SM}px;
  font-weight: 500;
  line-height: 1;
`;

const StartButton = styled(PlayButton)`
  margin-top: ${SPACE_2XL}px;
`;

const SecondaryButton = styled(CallToActionButton)`
  margin-top: ${SPACE_MD}px;

  :disabled {
    color: ${COLOR_GRAY_DISABLED};
  }
`;

const ShareSwitch = styled(Form)`
  display: flex;
  justify-content: center;
  margin-top: ${SPACE_MD}px;
`;

const DetailsAwards = styled(LessonAwards)<{ $isWide: boolean }>`
  width: 100%;
  margin-top: ${(p) => (p.$isWide ? 0 : SPACE_2XL)}px;
`;

const DescriptionLabel = styled(SectionLabel)`
  margin-top: ${SPACE_2XL}px;
`;

const Description = styled.div`
  margin-top: ${SPACE_LG}px;
  color: ${COLOR_TEXT_SECONDARY};
  font-size: 14px;
  font-weight: 500;
  line-height: 24px;
  white-space: pre-wrap;
`;

export const LessonPage: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { lessonId }: { lessonId: string } = useParams<{
    lessonId: string;
  }>();
  const { customFlashpackId }: { customFlashpackId?: string } = useParams<{
    customFlashpackId?: string;
  }>();
  const [fetchedLesson, setFetchedLesson] = React.useState<{
    lesson: Lesson;
    profileId: number | null;
  } | null>(null);
  const lesson = fetchedLesson?.lesson ?? null;
  const { getLesson, modifyCustomFlashpack } = React.useContext(ApiContext);
  const location = useLocation();
  const [unitId, setUnitId] = React.useState<number>();
  const [account, setAccount] = React.useState<Account>();
  const { accountLiveData, forceReloadUserAndProfile } =
    React.useContext(AccountContext);
  const { startLoading, stopLoading } = useLoadingIndicator(true);
  const lessonFetchedForProfileStateVersion = React.useRef<number | null>(null);
  const fetchJobForProfileStateVersion = React.useRef<number | null>(null);
  const shouldLogViewLessonStart = React.useRef<boolean>(true);
  const [startButtonState, setStartButtonState] = React.useState(
    StartButtonState.UPGRADE
  );
  const [showPrintables, setShowPrintables] = React.useState(false);
  const [showStandards, setShowStandards] = React.useState(false);
  const [userIsSchoolTeacher, setUserIsSchoolTeacher] =
    React.useState<boolean>();
  const [isShareDialogOpen, setIsShareDialogOpen] = React.useState(false);
  const [lessonExecutionState, setLessonExecutionState] =
    React.useState<LessonExecutionState>({
      type: "BEFORE_PLAY_LESSON_NOT_YET_STARTED",
    });
  const currentFetchJobUuid = React.useRef<string | null>(null);
  const lessonFetchedForLessonId = React.useRef<number | null>(null);
  const fetchJobForLessonId = React.useRef<number | null>(null);
  const lessonFetchedForCustomFlashpackId = React.useRef<number | null>(null);
  const fetchJobForCustomFlashpackId = React.useRef<number | null>(null);
  const lessonFetchedForProfileId = React.useRef<number | null>(null);
  const fetchJobForProfileId = React.useRef<number | null>(null);

  const flashpackEditMenuAnchorRef = React.useRef<HTMLButtonElement>(null);
  const isWide = useMediaQuery(WIDE_LAYOUT_QUERY);
  const [isFlashpackEditMenuOpen, setIsFlashpackEditMenuOpen] =
    React.useState(false);
  // Visually disable the checkbox while the modifyCustomFlashpack API call is in progress
  const [
    isFlashpackSharingCheckboxDisabled,
    setIsFlashpackSharingCheckboxDisabled,
  ] = React.useState(false);
  const { logEvent } = React.useContext(AnalyticsContext);
  const [isSingleLessonUnassignModalOpen, setIsSingleLessonUnassignModalOpen] =
    React.useState(false);
  const [isAssignedByThisTeacher, setIsAssignedByThisTeacher] =
    React.useState(false);

  const setLessonExecutionStateWrapper = (newState: LessonExecutionState) => {
    setLessonExecutionState((previousState) => {
      if (
        newState.type === "BEFORE_PLAY_LESSON_NOT_YET_STARTED" &&
        newState !== previousState
      ) {
        shouldLogViewLessonStart.current = true;
      }
      return newState;
    });
  };

  const fetchLessonIfStale = React.useCallback(
    async (
      account: Account,
      force: boolean,
      enableLoadingIndicator: boolean
    ) => {
      const lessonIdAsNumber = +lessonId;
      const customFlashpackIdAsNumber = customFlashpackId
        ? +customFlashpackId
        : null;
      const profileId = account.profile?.id ?? null;
      if (
        force ||
        (account.profileStateCurrentVersion !==
          lessonFetchedForProfileStateVersion.current &&
          account.profileStateCurrentVersion !==
            fetchJobForProfileStateVersion.current) ||
        (lessonIdAsNumber !== lessonFetchedForLessonId.current &&
          lessonIdAsNumber !== fetchJobForLessonId.current) ||
        (customFlashpackIdAsNumber !==
          lessonFetchedForCustomFlashpackId.current &&
          customFlashpackIdAsNumber !== fetchJobForCustomFlashpackId.current) ||
        (profileId !== lessonFetchedForProfileId.current &&
          profileId !== fetchJobForProfileId.current)
      ) {
        if (enableLoadingIndicator) {
          startLoading();
        }
        const fetchJobUuid = (currentFetchJobUuid.current = UUID.v4());
        fetchJobForLessonId.current = lessonIdAsNumber;
        fetchJobForCustomFlashpackId.current = customFlashpackIdAsNumber;
        fetchJobForProfileId.current = profileId;

        const versionForLessonInProgress = getVersionForLessonInProgress(
          account.profileState?.lessons_in_progress || {},
          +lessonId,
          customFlashpackId ? +customFlashpackId : null
        );
        fetchJobForProfileStateVersion.current =
          account.profileStateCurrentVersion;

        const lesson = await getLesson(
          +lessonId,
          versionForLessonInProgress,
          customFlashpackId ? +customFlashpackId : null
        );

        if (currentFetchJobUuid.current !== fetchJobUuid) {
          return;
        }
        setFetchedLesson({ lesson, profileId });
        lessonFetchedForProfileId.current = profileId;
        lessonFetchedForLessonId.current = lesson.id;
        lessonFetchedForCustomFlashpackId.current = lesson.custom_flashpack_id;
        setStartButtonState(
          !lesson.is_available_in_trial && isInTrialMode(account.user)
            ? StartButtonState.UPGRADE
            : versionForLessonInProgress != null
              ? StartButtonState.CONTINUE
              : StartButtonState.START
        );
        setIsAssignedByThisTeacher(lesson.is_assigned_by_this_teacher);
        lessonFetchedForProfileStateVersion.current =
          fetchJobForProfileStateVersion.current;
        if (enableLoadingIndicator) {
          stopLoading();
        }
      }
    },
    [lessonId, customFlashpackId, getLesson, startLoading, stopLoading]
  );

  React.useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const queryUnitIdString = searchParams.get("unitId");
    const queryUnitId = queryUnitIdString ? +queryUnitIdString : undefined;
    setUnitId(location.state?.unitId || queryUnitId);
  }, [location, setUnitId]);

  useLiveData(
    React.useCallback(
      async (account: Account) => {
        if (!lessonId) {
          return;
        }
        if (account.hasMaybeProfileState()) {
          setUserIsSchoolTeacher(isSchoolUserTeacher(account.user));
          setAccount(account);
          await fetchLessonIfStale(account, false, true);
        }
      },
      [lessonId, fetchLessonIfStale]
    ),
    accountLiveData
  );

  function continueAfterCantPlay() {
    forceReloadUserAndProfile();
    if (
      (lessonExecutionState as LessonExecutionStateAfterPlay).cantPlay
        ?.action === CantPlayAction.GoBackBeforeLessonDetails
    ) {
      history.goBack();
    } else {
      setLessonExecutionStateWrapper({
        type: "BEFORE_PLAY_LESSON_NOT_YET_STARTED",
      });
    }
  }

  if (!account || !lesson) {
    return <></>;
  }

  if (shouldLogViewLessonStart.current) {
    logEvent(new ViewLessonStartEvent(lesson.id, lesson.title));
    shouldLogViewLessonStart.current = false;
  }

  const isCompleted = didProfileCompleteLesson(
    account.profileState?.completed_lessons_info || {},
    lesson.id,
    lesson.custom_flashpack_id
  );
  const description = isSchoolUserTeacher(account.user)
    ? lesson.teacher_long_description
    : lesson.long_description;
  const awards = (
    <DetailsAwards
      $isWide={isWide}
      lesson={lesson}
      account={account}
      isCompleted={isCompleted}
    />
  );

  return (
    <>
      {lessonExecutionState.type === "PLAYING" ? (
        <LessonPagePlayer
          unitId={unitId}
          lesson={lesson}
          lessonProfileId={fetchedLesson?.profileId ?? null}
          setLessonExecutionState={setLessonExecutionStateWrapper}
          account={account}
        />
      ) : [
          "BEFORE_PLAY_LESSON_NOT_YET_STARTED",
          "BEFORE_PLAY_FIRSTNAME",
        ].includes(lessonExecutionState.type) ? (
        <>
          <BackNavRow
            label={unitId ? t("lesson_nav_back") : t("generic_nav_back")}
            rightChildren={
              <ToolbarActions>
                <Dropdown>
                  <ToolbarDropdownToggle
                    variant="link"
                    aria-label={t("lesson_details_share_dropdown_copy_link")}
                  >
                    <FontAwesomeIcon icon={faArrowUpFromSquare} />
                  </ToolbarDropdownToggle>
                  <Dropdown.Menu>
                    <Dropdown.Item
                      onClick={() =>
                        navigator.clipboard.writeText(lesson.universal_link)
                      }
                    >
                      {t("lesson_details_share_dropdown_copy_link")}
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
                {lesson.downloadable_materials.length > 0 && (
                  <ToolbarButton
                    aria-label={t("printables_title")}
                    onClick={() => {
                      logEvent(
                        new LessonMaterialsButtonTapEvent(
                          lesson.id,
                          lesson.title
                        )
                      );
                      setShowPrintables(true);
                    }}
                  >
                    <FontAwesomeIcon icon={faPrint} />
                  </ToolbarButton>
                )}
                {/* Standards live on real lessons only, never on
                    user-created flashpacks. */}
                {lesson.custom_flashpack_id === null &&
                  !lesson.is_studio_project && (
                    <ToolbarButton
                      aria-label={t("standards_modal_title")}
                      onClick={() => {
                        logEvent(
                          new LessonStandardsButtonTapEvent(
                            lesson.id,
                            lesson.title
                          )
                        );
                        setShowStandards(true);
                      }}
                    >
                      <FontAwesomeIcon icon={faGraduationCap} />
                    </ToolbarButton>
                  )}
                {lesson.show_edit_ui && (
                  <ToolbarButton
                    ref={flashpackEditMenuAnchorRef}
                    aria-label={t("lesson_details_flashpack_options")}
                    onClick={() => {
                      if (!isFlashpackEditMenuOpen) {
                        logEvent(
                          new LessonStartEditFlashpackButtonTapEvent(
                            lesson.id,
                            lesson.custom_flashpack_id!
                          )
                        );
                      }
                      setIsFlashpackEditMenuOpen(!isFlashpackEditMenuOpen);
                    }}
                  >
                    <FontAwesomeIcon icon={faCircleEllipsis} />
                  </ToolbarButton>
                )}
              </ToolbarActions>
            }
          />
          {lesson.show_edit_ui && (
            <FlashpackEditMenu
              anchor={flashpackEditMenuAnchorRef.current!}
              isOpen={isFlashpackEditMenuOpen}
              onClose={() => setIsFlashpackEditMenuOpen(false)}
              onFlashpackDeleted={() => {
                // We can't be on this page anymore because API will 404 from now on.
                history.replace(TabEnum.Practice);
              }}
              onFlashpackRenamed={() => fetchLessonIfStale(account, true, true)}
              onShowSettings={() => {
                setLessonExecutionStateWrapper({
                  type: "BEFORE_PLAY_SHOW_FLASHPACK_SETTINGS",
                });
              }}
              lesson={lesson}
            />
          )}
          <DetailsLayout $isWide={isWide}>
            <CoverColumn $isWide={isWide}>
              <DetailsCover
                $isWide={isWide}
                url={lesson.cover_image}
                alt={t("lesson_cover_content_description", {
                  title: lesson.title,
                })}
                isElevated
                lazy={false}
              />
              {isWide && awards}
            </CoverColumn>
            <DetailsColumn $isWide={isWide}>
              <DetailsTitle>{lesson.title}</DetailsTitle>
              <DetailsMetaRow $gap={SPACE_LG}>
                {!lesson.is_studio_project && (
                  <CellMetaItem icon="duration">
                    {t("lesson_duration_in_minutes", {
                      length: lesson.length_minutes,
                    })}
                  </CellMetaItem>
                )}
                {!lesson.is_studio_project && hasAgeRange(lesson) && (
                  <CellMetaItem icon="ageRange">
                    {ageRange(t, lesson.age_min, lesson.age_max)}
                  </CellMetaItem>
                )}
                {isCompleted && <CompletedMetaItem />}
              </DetailsMetaRow>
              <StartButton
                onClick={() => {
                  logEvent(
                    new StartLessonButtonTapEvent(
                      lesson.id,
                      lesson.title,
                      lesson.is_studio_project,
                      unitId
                    )
                  );
                  if (
                    !lesson.is_available_in_trial &&
                    isInTrialMode(account.user)
                  ) {
                    history.push("/account/upgrade/");
                  } else {
                    setLessonExecutionStateWrapper({ type: "PLAYING" });
                  }
                }}
              >
                <FontAwesomeIcon icon={faPlay} />
                {t(
                  startButtonState === StartButtonState.CONTINUE
                    ? "lesson_details_continue"
                    : "lesson_details_start"
                )}
              </StartButton>
              {userIsSchoolTeacher && (
                <>
                  <SecondaryButton
                    onClick={() => {
                      logEvent(
                        new AssignLessonButtonTapEvent(
                          lesson.id,
                          lesson.title,
                          unitId
                        )
                      );
                      setLessonExecutionStateWrapper({
                        type: "BEFORE_PLAY_ASSIGN_LESSON",
                      });
                    }}
                  >
                    <FontAwesomeIcon icon={faPaperPlaneTop} />
                    {t("lesson_details_assign")}
                  </SecondaryButton>
                  <SecondaryButton
                    onClick={() => {
                      logEvent(
                        new UnassignLessonButtonTapEvent(
                          lesson.id,
                          lesson.title,
                          unitId
                        )
                      );
                      setIsSingleLessonUnassignModalOpen(true);
                    }}
                    disabled={!isAssignedByThisTeacher}
                  >
                    <FontAwesomeIcon icon={faUserXmark} />
                    {t("lesson_details_unassign")}
                  </SecondaryButton>
                </>
              )}
              {userIsSchoolTeacher && lesson.show_edit_ui && (
                <ShareSwitch>
                  <Form.Check
                    type="switch"
                    label={t("lesson_details_share_with_other_teachers")}
                    checked={lesson.shared}
                    disabled={isFlashpackSharingCheckboxDisabled}
                    onChange={(event) => {
                      if (event.target.checked) {
                        setIsShareDialogOpen(true);
                      } else {
                        setIsFlashpackSharingCheckboxDisabled(true);
                        modifyCustomFlashpack(lesson.custom_flashpack_id!, {
                          share_with_school: false,
                        })
                          .then(() => fetchLessonIfStale(account, true, false))
                          .then(() =>
                            setIsFlashpackSharingCheckboxDisabled(false)
                          );
                      }
                    }}
                  />
                </ShareSwitch>
              )}
              {!isWide && awards}
              {description && (
                <>
                  <DescriptionLabel>
                    {t("lesson_details_in_this_lesson")}
                  </DescriptionLabel>
                  <Description>{description}</Description>
                </>
              )}
            </DetailsColumn>
          </DetailsLayout>
          <ConfirmRenameAndShareFlashpackModal
            isVisible={isShareDialogOpen}
            initialTitle={lesson.title}
            onSubmit={(newTitle) => {
              setIsShareDialogOpen(false);
              setIsFlashpackSharingCheckboxDisabled(true);
              modifyCustomFlashpack(lesson.custom_flashpack_id!, {
                share_with_school: true,
                title: newTitle,
              })
                .then(() => fetchLessonIfStale(account, true, true))
                .then(() => setIsFlashpackSharingCheckboxDisabled(false));
            }}
            onCancel={() => setIsShareDialogOpen(false)}
          />
          <PrintablesModal
            lesson={lesson}
            show={showPrintables}
            onHide={() => setShowPrintables(false)}
          />
          <LessonStandardsModal
            lessonId={lesson.id}
            show={showStandards}
            onHide={() => setShowStandards(false)}
            subdivisionCode={account.user.flags["subdivision_code"]}
            // Same gate as search feedback — stays beta-tester-only even
            // after the standards UI itself launches to everyone.
            canGiveFeedback={account.user.full_access === FullAccess.BetaTester}
          />
          <SingleLessonUnassignModal
            lesson={lesson}
            customFlashpackId={customFlashpackId ? +customFlashpackId : null}
            show={isSingleLessonUnassignModalOpen}
            onClose={(hasAssignments) => {
              if (hasAssignments !== null) {
                setIsAssignedByThisTeacher(hasAssignments);
              }
              setIsSingleLessonUnassignModalOpen(false);
            }}
          />
        </>
      ) : lessonExecutionState.type === "AFTER_PLAY" &&
        lessonExecutionState.cantPlay === null ? (
        <LessonPageEndLessonScreen
          lesson={lesson}
          setLessonExecutionState={setLessonExecutionStateWrapper}
          account={account}
        />
      ) : lessonExecutionState.type === "BEFORE_PLAY_SHOW_FLASHPACK_SETTINGS" &&
        customFlashpackId !== undefined ? (
        <FlashpackSettings
          customFlashpackId={+customFlashpackId}
          lesson={lesson}
          onCanceled={() => {
            setLessonExecutionStateWrapper({
              type: "BEFORE_PLAY_LESSON_NOT_YET_STARTED",
            });
          }}
          onChanged={() => {
            fetchLessonIfStale(account, true, true).then(() => {
              setLessonExecutionStateWrapper({
                type: "BEFORE_PLAY_LESSON_NOT_YET_STARTED",
              });
            });
          }}
        />
      ) : lessonExecutionState.type === "BEFORE_PLAY_ASSIGN_LESSON" ? (
        <LessonAssignmentsPage
          initialLesson={lesson}
          initialUnitId={unitId}
          onCancel={() =>
            setLessonExecutionStateWrapper({
              type: "BEFORE_PLAY_LESSON_NOT_YET_STARTED",
            })
          }
        />
      ) : (
        <></>
      )}
      <Modal
        show={
          lessonExecutionState.type === "AFTER_PLAY" &&
          lessonExecutionState.cantPlay !== null
        }
        onHide={() => {
          continueAfterCantPlay();
        }}
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {
              (lessonExecutionState as LessonExecutionStateAfterPlay).cantPlay
                ?.title
            }
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {
            (lessonExecutionState as LessonExecutionStateAfterPlay).cantPlay
              ?.description
          }
          {(lessonExecutionState as LessonExecutionStateAfterPlay).cantPlay
            ?.image_url && (
            <>
              <br />
              <img
                className="mx-auto my-3 d-block"
                style={{ maxWidth: "100%", maxHeight: "20em" }}
                alt={
                  (lessonExecutionState as LessonExecutionStateAfterPlay)
                    .cantPlay!.description
                }
                src={
                  (lessonExecutionState as LessonExecutionStateAfterPlay)
                    .cantPlay!.image_url!
                }
              />
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            onClick={() => {
              continueAfterCantPlay();
            }}
          >
            {t("cant_play_continue_button")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
